import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import InvestmentAssessment from '@/components/InvestmentAssessment';
import { properties } from '@/data/properties';
import { Link } from 'react-router-dom';
import {
  Crown,
  Lock,
  Eye,
  EyeOff,
  Shield,
  MapPin,
  Bed,
  Bath,
  Maximize,
  ArrowRight,
  Sparkles,
  Building2,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Simulated off-market properties (enhanced versions of existing with "exclusive" status)
const offMarketProperties = [
  {
    id: 'private-monaco-penthouse',
    title: 'Confidential Monaco Residence',
    location: 'Monaco',
    price: 'Price Upon Request',
    priceHint: '€55M+',
    beds: 6,
    baths: 7,
    sqm: '1,200',
    exclusive: true,
    description: 'Triple-height penthouse with private rooftop helipad. Never publicly listed.',
  },
  {
    id: 'private-dubai-villa',
    title: 'Emirates Hills Private Compound',
    location: 'Dubai, UAE',
    price: 'Price Upon Request',
    priceHint: '€35M+',
    beds: 12,
    baths: 15,
    sqm: '3,500',
    exclusive: true,
    description: 'Ultra-private 3-acre compound with golf course views. Diplomatic owner.',
  },
  {
    id: 'private-como-estate',
    title: 'Lake Como Historic Estate',
    location: 'Lake Como, Italy',
    price: 'Price Upon Request',
    priceHint: '€42M+',
    beds: 14,
    baths: 16,
    sqm: '2,800',
    exclusive: true,
    description: '18th century palazzo with 200m private lakefront. First time on market in 60 years.',
  },
  {
    id: 'private-ibiza-compound',
    title: 'Ibiza Clifftop Estate',
    location: 'Ibiza, Spain',
    price: 'Price Upon Request',
    priceHint: '€28M+',
    beds: 8,
    baths: 10,
    sqm: '1,800',
    exclusive: true,
    description: 'Complete privacy on 15-hectare estate with helipad and private beach.',
  },
];

const PrivatePortal = () => {
  const [isQualified, setIsQualified] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [revealedProperties, setRevealedProperties] = useState<string[]>([]);

  // Check qualification status from session
  useEffect(() => {
    const qualified = sessionStorage.getItem('portalQualified');
    if (qualified === 'true') {
      setIsQualified(true);
    }
  }, []);

  const handleAssessmentComplete = () => {
    setIsQualified(true);
    sessionStorage.setItem('portalQualified', 'true');
    setShowAssessment(false);
  };

  const togglePropertyReveal = (propertyId: string) => {
    if (revealedProperties.includes(propertyId)) {
      setRevealedProperties(revealedProperties.filter(id => id !== propertyId));
    } else {
      setRevealedProperties([...revealedProperties, propertyId]);
    }
  };

  return (
    <>
      <Helmet>
        <title>Private Client Portal | Agota Estate - Off-Market Luxury Properties</title>
        <meta name="description" content="Exclusive access to off-market luxury properties for qualified investors. Monaco, Dubai, Italy, Spain - properties not available to the public." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <main className="min-h-screen bg-background text-foreground">
        <Navbar />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-gold/5" />
          <div className="absolute top-0 right-0 w-1/2 h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-gold/10 via-transparent to-transparent" />

          <div className="container relative z-10 mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="flex items-center justify-center gap-2 mb-6">
                <Crown className="h-5 w-5 text-gold" />
                <span className="text-sm font-medium text-gold uppercase tracking-wider">Private Client Portal</span>
              </div>

              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
                Off-Market <span className="text-gold">Exclusives</span>
              </h1>

              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Access properties that never reach public markets. Discretely curated for 
                discerning investors who value privacy and first-mover advantage.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 max-w-md mx-auto">
                <div className="text-center">
                  <p className="text-2xl font-display text-gold">€2B+</p>
                  <p className="text-xs text-muted-foreground">Portfolio Value</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-display text-gold">47</p>
                  <p className="text-xs text-muted-foreground">Private Listings</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-display text-gold">100%</p>
                  <p className="text-xs text-muted-foreground">Confidential</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-6 lg:px-12">
            <AnimatePresence mode="wait">
              {!isQualified ? (
                <motion.div
                  key="locked"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="max-w-4xl mx-auto"
                >
                  {/* Locked State */}
                  <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-center">
                    {/* Left - Preview of locked properties */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-6">
                        <Lock className="h-4 w-4 text-gold" />
                        <span className="text-sm text-muted-foreground">Preview of Available Properties</span>
                      </div>

                      {offMarketProperties.slice(0, 3).map((property, index) => (
                        <motion.div
                          key={property.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="relative p-4 rounded-xl bg-card/50 border border-border/50 blur-[2px] hover:blur-none transition-all duration-500 group"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gold/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-foreground">{property.title}</p>
                              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin className="h-3 w-3" />
                                {property.location}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gold font-medium">EXCLUSIVE</p>
                              <p className="text-sm text-muted-foreground">{property.priceHint}</p>
                            </div>
                          </div>
                        </motion.div>
                      ))}

                      <p className="text-center text-sm text-muted-foreground pt-4">
                        + {offMarketProperties.length - 3 + 43} more exclusive properties
                      </p>
                    </div>

                    {/* Right - Unlock CTA */}
                    <div className="relative">
                      <div className="p-8 rounded-2xl bg-gradient-to-br from-secondary via-card to-background border border-gold/30">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-2xl" />

                        <div className="relative z-10">
                          <div className="w-16 h-16 rounded-full bg-gradient-gold flex items-center justify-center mx-auto mb-6">
                            <Lock className="h-8 w-8 text-cream" />
                          </div>

                          <h2 className="text-2xl font-display text-center text-foreground mb-4">
                            Unlock Full Access
                          </h2>

                          <p className="text-center text-muted-foreground mb-6">
                            Complete a brief investment assessment to access our complete off-market portfolio 
                            and connect with a dedicated advisor.
                          </p>

                          <ul className="space-y-3 mb-8">
                            <li className="flex items-center gap-3 text-sm text-foreground">
                              <Sparkles className="h-4 w-4 text-gold flex-shrink-0" />
                              <span>47 off-market properties</span>
                            </li>
                            <li className="flex items-center gap-3 text-sm text-foreground">
                              <Building2 className="h-4 w-4 text-gold flex-shrink-0" />
                              <span>Properties from €5M to €100M+</span>
                            </li>
                            <li className="flex items-center gap-3 text-sm text-foreground">
                              <TrendingUp className="h-4 w-4 text-gold flex-shrink-0" />
                              <span>First access to new listings</span>
                            </li>
                            <li className="flex items-center gap-3 text-sm text-foreground">
                              <Shield className="h-4 w-4 text-gold flex-shrink-0" />
                              <span>Complete confidentiality</span>
                            </li>
                          </ul>

                          <Button
                            onClick={() => setShowAssessment(true)}
                            className="w-full bg-gradient-gold text-cream hover:opacity-90 h-12"
                            size="lg"
                          >
                            Begin Assessment
                            <ArrowRight className="h-4 w-4 ml-2" />
                          </Button>

                          <p className="text-center text-xs text-muted-foreground mt-4">
                            2 minutes • Completely confidential
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Assessment Modal */}
                  <AnimatePresence>
                    {showAssessment && (
                      <>
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="fixed inset-0 bg-background/90 backdrop-blur-md z-50"
                          onClick={() => setShowAssessment(false)}
                        />
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl max-h-[90vh] overflow-y-auto z-50 p-4"
                        >
                          <InvestmentAssessment variant="modal" onComplete={handleAssessmentComplete} />
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </motion.div>
              ) : (
                <motion.div
                  key="unlocked"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {/* Unlocked State - Full access */}
                  <div className="flex items-center justify-center gap-2 mb-8">
                    <Shield className="h-4 w-4 text-gold" />
                    <span className="text-sm text-gold uppercase tracking-wider">Full Access Granted</span>
                  </div>

                  {/* Off-Market Properties Grid */}
                  <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-16">
                    {offMarketProperties.map((property, index) => {
                      const isRevealed = revealedProperties.includes(property.id);

                      return (
                        <motion.div
                          key={property.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="group relative overflow-hidden rounded-2xl bg-card border border-border/50 hover:border-gold/30 transition-all duration-500"
                        >
                          {/* Exclusive badge */}
                          <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full bg-gradient-gold text-cream text-xs font-medium">
                            EXCLUSIVE
                          </div>

                          {/* Privacy toggle */}
                          <button
                            onClick={() => togglePropertyReveal(property.id)}
                            className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm border border-border flex items-center justify-center hover:bg-gold/10 transition-colors"
                          >
                            {isRevealed ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-gold" />
                            )}
                          </button>

                          {/* Image placeholder */}
                          <div className="relative h-48 bg-gradient-to-br from-secondary to-muted overflow-hidden">
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Lock className="h-12 w-12 text-muted-foreground/50" />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                          </div>

                          {/* Content */}
                          <div className="p-6">
                            <h3 className={`font-display text-xl text-foreground mb-2 transition-all duration-300 ${!isRevealed ? 'blur-sm select-none' : ''}`}>
                              {property.title}
                            </h3>

                            <p className="flex items-center gap-1 text-sm text-muted-foreground mb-4">
                              <MapPin className="h-3.5 w-3.5" />
                              {property.location}
                            </p>

                            <p className={`text-sm text-muted-foreground mb-4 transition-all duration-300 ${!isRevealed ? 'blur-sm select-none' : ''}`}>
                              {property.description}
                            </p>

                            {/* Stats */}
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                              <span className="flex items-center gap-1">
                                <Bed className="h-4 w-4" />
                                {property.beds}
                              </span>
                              <span className="flex items-center gap-1">
                                <Bath className="h-4 w-4" />
                                {property.baths}
                              </span>
                              <span className="flex items-center gap-1">
                                <Maximize className="h-4 w-4" />
                                {property.sqm}m²
                              </span>
                            </div>

                            {/* Price */}
                            <div className="flex items-center justify-between pt-4 border-t border-border/50">
                              <div>
                                <p className="text-gold font-medium">{property.price}</p>
                                <p className="text-xs text-muted-foreground">Est. {property.priceHint}</p>
                              </div>
                              <Button variant="outline" size="sm" className="border-gold/30 text-foreground hover:bg-gold/10">
                                Request Details
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Featured Public Properties */}
                  <div className="border-t border-border pt-16">
                    <h2 className="font-display text-2xl text-foreground mb-8 text-center">
                      Featured Public Listings
                    </h2>

                    <div className="grid md:grid-cols-3 gap-6">
                      {properties.filter(p => p.featured).slice(0, 3).map((property) => (
                        <Link
                          key={property.id}
                          to={`/property/${property.id}`}
                          className="group block"
                        >
                          <div className="relative overflow-hidden rounded-xl aspect-[4/3] mb-3">
                            <img
                              src={property.image}
                              alt={property.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                          </div>
                          <h3 className="font-medium text-foreground group-hover:text-gold transition-colors">
                            {property.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">{property.location}</p>
                          <p className="text-gold font-medium mt-1">{property.price}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default PrivatePortal;
